/**
 * Amendment operations over solver results: apply an edit to the current stop
 * sequences and evaluate its cost delta + feasibility.
 *
 * Amendment payloads reference stable ENTITY ids (tripId / locationId /
 * vehicleId) — never route_stops row ids, which are regenerated on every
 * scenario reload.
 *
 * Op shapes:
 *  { type:'reorder',  vehicleId, fromIndex, toIndex }        // indices into the vehicle's WORK rows
 *  { type:'move',     tripId|locationId, fromVehicleId, toVehicleId,
 *                     pickupIndex?, deliveryIndex? }         // PD moves the pickup+delivery pair
 *  { type:'unassign', tripId|locationId, vehicleId }
 *  { type:'assign',   tripId|locationId, toVehicleId, pickupIndex?, deliveryIndex? }
 *
 * Indices are positions within the target vehicle's work rows (depot rows
 * excluded); deliveryIndex is the position AFTER the pickup has been inserted.
 * Omitted indices append at the end (pickup) / directly after the pickup.
 */

import { evaluateRoute, groupRouteStops, regenerateRouteRows } from './evaluator';

const numOr = (v, fallback) => {
  const n = Number(v);
  return v == null || v === '' || !Number.isFinite(n) ? fallback : n;
};

const isDepotRow = (row) => row.stopType === 'route_start' || row.stopType === 'route_end';

/** Split a route into [startRows, workRows(incl. restock), endRows]. */
function splitRoute(rows) {
  const start = rows.filter((r) => r.stopType === 'route_start');
  const end = rows.filter((r) => r.stopType === 'route_end');
  const work = rows.filter((r) => !isDepotRow(r));
  return { start, work, end };
}

const entityMatches = (row, payload) => {
  if (payload.tripId != null) return String(row.tripId) === String(payload.tripId);
  if (payload.locationId != null) return String(row.locationId) === String(payload.locationId) && row.tripId == null;
  return false;
};

/** Synthesize depot rows for a vehicle that has no route yet. */
function synthesizeDepotRows(vehicleId, ctx) {
  const vehicle = ctx.vehicleInfo(vehicleId);
  const startLoc = vehicle && vehicle.startLocationId != null ? ctx.locationsById.get(String(vehicle.startLocationId)) : null;
  const base = {
    locationId: startLoc ? startLoc.UUID : null,
    tripId: null,
    vehicleId: String(vehicleId),
    serviceDuration: 0,
    capacityServed: 0,
    locationName: startLoc ? startLoc.locationName : null,
    color: (vehicle && vehicle.color) || null,
    action: null,
  };
  return {
    start: [{ ...base, stopType: 'route_start' }],
    end: [{ ...base, stopType: 'route_end' }],
  };
}

const actionLabel = (verb, demand, locationName) =>
  `${verb} ${numOr(demand, 1)} at location: ${locationName ?? 'unknown'}`;

/** Build pickup(+delivery) work rows for a trip/location being (re)assigned. */
function buildEntityRows(payload, vehicleId, ctx) {
  if (payload.tripId != null) {
    const trip = ctx.tripsById.get(String(payload.tripId));
    if (!trip) return null;
    const from = ctx.locationsById.get(String(trip.fromLocationId));
    const to = trip.toLocationId != null ? ctx.locationsById.get(String(trip.toLocationId)) : null;
    const demand = numOr(trip.demand, 1);
    const pickup = {
      locationId: trip.fromLocationId,
      tripId: trip.UUID,
      vehicleId: String(vehicleId),
      stopType: 'pickup',
      serviceDuration: numOr(trip.pickupServiceDuration, 0),
      capacityServed: demand,
      locationName: from ? from.locationName : null,
      color: null,
      action: actionLabel('Pick up', demand, from && from.locationName),
    };
    if (!to) return { rows: [pickup] };
    const delivery = {
      ...pickup,
      locationId: trip.toLocationId,
      stopType: 'delivery',
      serviceDuration: numOr(trip.deliveryServiceDuration, 0),
      capacityServed: -demand,
      locationName: to.locationName,
      action: actionLabel('Deliver', demand, to.locationName),
    };
    return { rows: [pickup, delivery] };
  }
  const loc = ctx.locationsById.get(String(payload.locationId));
  if (!loc) return null;
  return {
    rows: [
      {
        locationId: loc.UUID,
        tripId: null,
        vehicleId: String(vehicleId),
        stopType: 'location_visit',
        serviceDuration: numOr(loc.serviceDuration, 0),
        capacityServed: numOr(loc.demand, 1),
        locationName: loc.locationName,
        color: null,
        action: actionLabel('Complete service of', loc.demand, loc.locationName),
      },
    ],
  };
}

/** Backend-shaped dropped rows for an unassigned entity. */
function buildDroppedRows(payload, ctx) {
  if (payload.tripId != null && !ctx.tripsById.has(String(payload.tripId)) && payload.locationId != null) {
    // Vehicle-routing rows carry backend-synthesized trip ids that don't exist
    // in the trips table — the dropped entity is really the location.
    return buildDroppedRows({ locationId: payload.locationId }, ctx);
  }
  if (payload.tripId != null) {
    const trip = ctx.tripsById.get(String(payload.tripId));
    if (!trip) return { trips: [], locations: [] };
    const from = ctx.locationsById.get(String(trip.fromLocationId));
    const to = trip.toLocationId != null ? ctx.locationsById.get(String(trip.toLocationId)) : null;
    return {
      trips: [
        {
          tripId: trip.UUID,
          tripName: trip.tripId != null ? String(trip.tripId) : trip.UUID,
          demandDropped: numOr(trip.demand, 1),
          capacityServed: numOr(trip.demand, 1),
          fromLocationName: from ? from.locationName : null,
          toLocationName: to ? to.locationName : null,
          fromLocationId: trip.fromLocationId,
          toLocationId: trip.toLocationId ?? null,
          fromLatitude: from ? from.latitude : null,
          fromLongitude: from ? from.longitude : null,
          toLatitude: to ? to.latitude : null,
          toLongitude: to ? to.longitude : null,
          Valid: true,
          validationMessage: null,
        },
      ],
      locations: [],
    };
  }
  const loc = ctx.locationsById.get(String(payload.locationId));
  if (!loc) return { trips: [], locations: [] };
  return {
    trips: [],
    locations: [
      {
        locationId: loc.UUID,
        locationName: loc.locationName,
        demandDropped: numOr(loc.demand, 1),
        latitude: loc.latitude,
        longitude: loc.longitude,
        isDepot: Boolean(loc.isDepot),
        Valid: true,
        validationMessage: null,
      },
    ],
    };
}

/**
 * Apply an amendment to the grouped routes.
 * Returns { sequences: Map(vehicleId → full ordered rows), dropAdds, dropRemovals, error }.
 * Only AFFECTED vehicles appear in `sequences`; an empty array means the
 * vehicle's route disappears (it no longer serves any work stop).
 */
export function applyAmendment(byVehicle, amendment, ctx) {
  const sequences = new Map();
  const dropAdds = { trips: [], locations: [] };
  const dropRemovals = { tripIds: [], locationIds: [] };
  const fail = (message) => ({ sequences, dropAdds, dropRemovals, error: message });

  const routeOf = (vehicleId) => {
    const rows = byVehicle.get(String(vehicleId)) || [];
    const { start, work, end } = splitRoute(rows);
    if (start.length === 0 && end.length === 0 && rows.length === 0) {
      const depot = synthesizeDepotRows(vehicleId, ctx);
      return { start: depot.start, work: [], end: depot.end };
    }
    return { start, work, end };
  };
  const assemble = (vehicleId, route) => {
    sequences.set(
      String(vehicleId),
      route.work.length === 0 ? [] : [...route.start, ...route.work, ...route.end]
    );
  };

  switch (amendment.type) {
    case 'reorder': {
      const route = routeOf(amendment.vehicleId);
      const { fromIndex, toIndex } = amendment;
      if (fromIndex == null || toIndex == null || fromIndex < 0 || fromIndex >= route.work.length) {
        return fail('Reorder indices are out of range');
      }
      const work = [...route.work];
      const [moved] = work.splice(fromIndex, 1);
      if (moved.stopType === 'restock') return fail('Restock stops cannot be moved');
      work.splice(Math.max(0, Math.min(toIndex, work.length)), 0, moved);
      assemble(amendment.vehicleId, { ...route, work });
      return { sequences, dropAdds, dropRemovals, error: null };
    }

    case 'unassign': {
      const route = routeOf(amendment.vehicleId);
      const removed = route.work.filter((r) => entityMatches(r, amendment));
      if (removed.length === 0) return fail('Nothing to unassign for that trip/location on this vehicle');
      const work = route.work.filter((r) => !entityMatches(r, amendment));
      assemble(amendment.vehicleId, { ...route, work });
      const dropped = buildDroppedRows(amendment, ctx);
      dropAdds.trips.push(...dropped.trips);
      dropAdds.locations.push(...dropped.locations);
      return { sequences, dropAdds, dropRemovals, error: null };
    }

    case 'assign': {
      const route = routeOf(amendment.toVehicleId);
      const built = buildEntityRows(amendment, amendment.toVehicleId, ctx);
      if (!built) return fail('Unknown trip/location to assign');
      const work = [...route.work];
      insertRows(work, built.rows, amendment);
      assemble(amendment.toVehicleId, { ...route, work });
      if (amendment.tripId != null) dropRemovals.tripIds.push(String(amendment.tripId));
      else dropRemovals.locationIds.push(String(amendment.locationId));
      return { sequences, dropAdds, dropRemovals, error: null };
    }

    case 'move': {
      const fromRoute = routeOf(amendment.fromVehicleId);
      const moved = fromRoute.work.filter((r) => entityMatches(r, amendment));
      if (moved.length === 0) return fail('Nothing to move for that trip/location on this vehicle');
      const fromWork = fromRoute.work.filter((r) => !entityMatches(r, amendment));
      assemble(amendment.fromVehicleId, { ...fromRoute, work: fromWork });

      const toRoute = routeOf(amendment.toVehicleId);
      // Rows keep their identity fields but change vehicle; colors follow the vehicle.
      const targetVehicle = ctx.vehicleInfo(amendment.toVehicleId);
      const rehomed = moved.map((r) => ({
        ...r,
        vehicleId: String(amendment.toVehicleId),
        color: (targetVehicle && targetVehicle.color) || r.color,
      }));
      const work = [...toRoute.work];
      insertRows(work, rehomed, amendment);
      assemble(amendment.toVehicleId, { ...toRoute, work });
      return { sequences, dropAdds, dropRemovals, error: null };
    }

    default:
      return fail(`Unknown amendment type: ${amendment.type}`);
  }
}

/** Insert pickup(+delivery) rows at the requested work positions. */
function insertRows(work, rows, amendment) {
  const pickupAt = amendment.pickupIndex != null ? clamp(amendment.pickupIndex, 0, work.length) : work.length;
  work.splice(pickupAt, 0, rows[0]);
  if (rows.length > 1) {
    const deliveryAt =
      amendment.deliveryIndex != null ? clamp(amendment.deliveryIndex, pickupAt + 1, work.length) : pickupAt + 1;
    work.splice(deliveryAt, 0, rows[1]);
  }
  if (rows.length > 2) {
    // Defensive: any further rows follow the delivery in order.
    work.splice(work.indexOf(rows[1]) + 1, 0, ...rows.slice(2));
  }
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

const routeTotals = (rows) => ({
  cost: rows.reduce((a, r) => a + numOr(r.travelCostTo, 0), 0),
  duration: rows.reduce((a, r) => a + numOr(r.travelDurationFromPreviousStop, 0), 0),
  distance: rows.reduce((a, r) => a + numOr(r.travelDistanceFromPreviousStop, 0), 0),
});

// Backend "used" = the vehicle has at least one non-depot stop (restock counts).
const hasWorkStop = (rows) => rows.some((r) => !isDepotRow(r));

/**
 * Evaluate an amendment against current results.
 * Returns { error } or:
 * { newRowsByVehicle: Map(vehicleId → regenerated rows[]; [] removes the route),
 *   dropAdds, dropRemovals, evaluations, violations,
 *   costDelta: {travel, fixed, total, duration, distance} }
 */
export function evaluateAmendment(data, amendment, ctx) {
  const byVehicle = groupRouteStops(data.route_stops);
  const applied = applyAmendment(byVehicle, amendment, ctx);
  if (applied.error) return { error: applied.error };

  const newRowsByVehicle = new Map();
  const evaluations = [];
  const violations = [];
  let travelDelta = 0;
  let durationDelta = 0;
  let distanceDelta = 0;
  let fixedDelta = 0;

  for (const [vehicleId, sequence] of applied.sequences) {
    const beforeRows = byVehicle.get(vehicleId) || [];
    const before = routeTotals(beforeRows);
    const beforeUsed = hasWorkStop(beforeRows);
    const vehicle = ctx.vehicleInfo(vehicleId);
    const fixedCost = vehicle ? vehicle.fixedCost : 0;

    if (sequence.length === 0) {
      newRowsByVehicle.set(vehicleId, []);
      travelDelta -= before.cost;
      durationDelta -= before.duration;
      distanceDelta -= before.distance;
      if (beforeUsed) fixedDelta -= fixedCost;
      continue;
    }

    const evaluated = evaluateRoute(vehicleId, sequence, ctx);
    const rows = regenerateRouteRows(evaluated, ctx);
    newRowsByVehicle.set(vehicleId, rows);
    evaluations.push(evaluated);
    violations.push(...evaluated.violations);

    travelDelta += evaluated.totals.cost - before.cost;
    durationDelta += evaluated.totals.duration - before.duration;
    distanceDelta += evaluated.totals.distance - before.distance;
    const afterUsed = hasWorkStop(sequence);
    if (afterUsed && !beforeUsed) fixedDelta += fixedCost;
    if (!afterUsed && beforeUsed) fixedDelta -= fixedCost;
  }

  return {
    newRowsByVehicle,
    dropAdds: applied.dropAdds,
    dropRemovals: applied.dropRemovals,
    evaluations,
    violations,
    costDelta: {
      travel: travelDelta,
      fixed: fixedDelta,
      total: travelDelta + fixedDelta,
      duration: durationDelta,
      distance: distanceDelta,
    },
    error: null,
  };
}

/**
 * Scan every insertion position for a trip/location on one vehicle and return
 * the best placement: feasible placements first, then lowest total cost delta.
 * O(n²) placements × O(n) evaluation — a few thousand stop evaluations on the
 * largest seeded routes, comfortably synchronous.
 *
 * `baseType` is 'assign' (from the dropped list) or 'move' (from another
 * vehicle); extra payload fields (fromVehicleId, …) ride through untouched.
 */
export function findBestInsertion(data, basePayload, toVehicleId, ctx) {
  const byVehicle = groupRouteStops(data.route_stops);
  const rows = byVehicle.get(String(toVehicleId)) || [];
  const workLen = rows.filter((r) => !isDepotRow(r) && !entityMatches(r, basePayload)).length;
  const isPair = basePayload.tripId != null && tripHasDelivery(basePayload.tripId, ctx);

  let best = null;
  for (let pickupIndex = 0; pickupIndex <= workLen; pickupIndex++) {
    const deliveryMax = isPair ? workLen + 1 : pickupIndex + 1;
    for (let deliveryIndex = pickupIndex + 1; deliveryIndex <= deliveryMax; deliveryIndex++) {
      const candidate = { ...basePayload, toVehicleId: String(toVehicleId), pickupIndex, deliveryIndex };
      const result = evaluateAmendment(data, candidate, ctx);
      if (result.error) continue;
      const score = {
        feasible: result.violations.length === 0,
        cost: result.costDelta.total,
      };
      const better =
        !best ||
        (score.feasible && !best.score.feasible) ||
        (score.feasible === best.score.feasible && score.cost < best.score.cost);
      if (better) best = { amendment: candidate, result, score };
      if (!isPair) break; // single-row entities have one delivery slot per pickup slot
    }
  }
  return best; // null when the vehicle/entity is unknown
}

const tripHasDelivery = (tripId, ctx) => {
  const trip = ctx.tripsById.get(String(tripId));
  return Boolean(trip && trip.toLocationId != null);
};
